import React, { useEffect } from "react";
import { ArrowLeft, FileText, AlertCircle, DollarSign, Calendar, Wrench } from "lucide-react";
import { Button } from "./ui/button";
import { useNavigate } from "react-router-dom";

const AGB = () => {
  const navigate = useNavigate();
  
  // Set page-specific SEO
  useEffect(() => {
    document.title = "AGB | Floria Maler & Sanierung";
    
    // Update meta description
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', 'Allgemeine Geschäftsbedingungen von Floria Maler & Sanierung für Malerarbeiten in Baden-Württemberg.');
    }
    
    // Update canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', 'https://malerbwfestpreis.de/agb');
    }
    
    // Cleanup on unmount
    return () => {
      document.title = "Maler Baden-Württemberg | Wohnung streichen zum Festpreis";
      if (metaDesc) {
        metaDesc.setAttribute('content', 'Maler in Baden-Württemberg zum Festpreis: Wohnung streichen inkl. Material und Abdeckarbeiten. Airless-Technik. Angebot schnell per WhatsApp anfragen.');
      }
      if (canonical) {
        canonical.setAttribute('href', 'https://malerbwfestpreis.de/');
      }
    };
  }, []);
  
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-orange-500 text-white py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center mb-4">
            <Button
              onClick={() => navigate('/')}
              variant="ghost"
              className="text-white hover:bg-white/20 mr-4"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Zurück
            </Button>
          </div>
          <h1 className="text-3xl lg:text-4xl font-bold">Allgemeine Geschäftsbedingungen</h1>
          <p className="text-blue-100 text-lg mt-2">Unsere AGB für Malerarbeiten und Dienstleistungen</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Geltungsbereich */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8 border border-gray-100">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            <FileText className="mr-3 h-6 w-6 text-blue-600" />
            Geltungsbereich
          </h2>
          
          <div className="space-y-4">
            <p className="text-gray-700">
              Diese Allgemeinen Geschäftsbedingungen gelten für alle Verträge zwischen 
              <strong> Floria Maler & Sanierung</strong> und unseren Kunden über die Erbringung von 
              Malerarbeiten, Renovierungsdienstleistungen und verwandten Leistungen.
            </p>
            
            <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-600">
              <h3 className="font-semibold text-gray-900 mb-2">Unsere Firma:</h3>
              <p className="text-gray-700">
                Floria Maler & Sanierung<br/>
                Aixheimerstrasse 50/4<br/>
                78554 Aldingen<br/>
                Deutschland<br/>
                USt-ID: DE319110250
              </p>
            </div>
            
            <p className="text-gray-700">
              Abweichende Bedingungen des Kunden erkennen wir nicht an, es sei denn, 
              wir haben ausdrücklich schriftlich ihrer Geltung zugestimmt.
            </p>
          </div>
        </div>

        {/* Vertragsabschluss */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8 border border-gray-100">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            <Calendar className="mr-3 h-6 w-6 text-blue-600" />
            Vertragsabschluss & Termine
          </h2>
          
          <div className="space-y-6">
            <div>
              <h3 className="font-semibold text-lg text-gray-900 mb-3">Angebote und Vertragsabschluss</h3>
              <p className="text-gray-700 mb-3">
                Unsere Angebote sind freibleibend und unverbindlich. Ein Vertrag kommt erst durch 
                unsere schriftliche Auftragsbestätigung oder durch Beginn der Arbeiten zustande.
              </p>
              <p className="text-gray-700">
                Festpreise gelten nur für die im Angebot spezifizierten Leistungen und Räumlichkeiten. 
                Alle Preise verstehen sich netto zzgl. der gesetzlichen Mehrwertsteuer.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-lg text-gray-900 mb-3">Termine und Fristen</h3>
              <ul className="text-gray-700 space-y-2">
                <li>• Termine werden nach Möglichkeit eingehalten, sind aber nur bei ausdrücklicher schriftlicher Bestätigung verbindlich</li>
                <li>• Bei Verzögerungen durch höhere Gewalt oder durch den Kunden verschuldete Umstände verlängern sich die Fristen entsprechend</li>
                <li>• Terminverschiebungen durch den Kunden müssen mindestens 48 Stunden im Voraus mitgeteilt werden</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Leistungen */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8 border border-gray-100">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            <Wrench className="mr-3 h-6 w-6 text-blue-600" />
            Leistungen & Ausführung
          </h2>
          
          <div className="space-y-6">
            <div>
              <h3 className="font-semibold text-lg text-gray-900 mb-3">Leistungsumfang</h3>
              <p className="text-gray-700 mb-3">
                Der Leistungsumfang ergibt sich aus der schriftlichen Auftragsbestätigung. 
                Im Festpreis enthalten sind:
              </p>
              <ul className="text-gray-700 space-y-2">
                <li>• Professionelle Malerarbeiten mit Airless-Technik bis 2,60m Deckenhöhe</li>
                <li>• Hochwertige PROFITEC Farbe</li>
                <li>• Alle erforderlichen Abdeckarbeiten</li>
                <li>• Grundreinigung nach Abschluss der Arbeiten</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-lg text-gray-900 mb-3">Voraussetzungen</h3>
              <div className="bg-orange-50 p-4 rounded-lg border-l-4 border-orange-500">
                <p className="text-gray-700 mb-2">
                  <strong>Wichtiger Hinweis:</strong> Unsere Festpreise gelten ausschließlich für 
                  <strong> leere Räume ohne Möbel</strong> (apartamenti vuoti senza mobili).
                </p>
                <ul className="text-gray-700 space-y-1 mt-3">
                  <li>• Räume müssen vollständig geräumt und besenrein übergeben werden</li>
                  <li>• Strom- und Wasseranschluss müssen verfügbar sein</li>
                  <li>• Zugang zu den Räumlichkeiten muss gewährleistet sein</li>
                  <li>• Bei möblierten Räumen entstehen Mehrkosten für zusätzliche Abdeckarbeiten</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Preise und Zahlung */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8 border border-gray-100">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            <DollarSign className="mr-3 h-6 w-6 text-blue-600" />
            Preise und Zahlung
          </h2>
          
          <div className="space-y-6">
            <div>
              <h3 className="font-semibold text-lg text-gray-900 mb-3">Festpreis-Garantie</h3>
              <p className="text-gray-700 mb-3">
                Wir bieten Ihnen eine Festpreis-Garantie für die vereinbarten Leistungen. 
                Der Preis ist verbindlich, sofern sich der Leistungsumfang nicht ändert.
              </p>
              <p className="text-gray-700">
                Alle Preise verstehen sich als Nettopreise zzgl. der gesetzlichen Mehrwertsteuer.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-lg text-gray-900 mb-3">Zahlungsmodalitäten</h3>
              <ul className="text-gray-700 space-y-2">
                <li>• Bei Aufträgen bis 1.000€: Zahlung nach Fertigstellung binnen 14 Tagen</li>
                <li>• Bei Aufträgen über 1.000€: 30% Anzahlung, Rest nach Fertigstellung</li>
                <li>• Bei Zahlungsverzug werden Verzugszinsen von 9% über dem Basiszinssatz berechnet</li>
                <li>• Zahlungen per Überweisung, EC-Karte oder bar</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-lg text-gray-900 mb-3">Mehrkosten</h3>
              <p className="text-gray-700">
                Mehrkosten entstehen bei Änderungen des ursprünglichen Auftrags, zusätzlichen 
                Wünschen oder wenn die Voraussetzungen für den Festpreis nicht erfüllt sind 
                (z.B. möblierte Räume, Deckenhöhen über 2,60m, zusätzliche Vorarbeiten).
              </p>
            </div>
          </div>
        </div>

        {/* Gewährleistung */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8 border border-gray-100">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            <AlertCircle className="mr-3 h-6 w-6 text-blue-600" />
            Gewährleistung & Haftung
          </h2>
          
          <div className="space-y-6">
            <div>
              <h3 className="font-semibold text-lg text-gray-900 mb-3">Gewährleistung</h3>
              <p className="text-gray-700 mb-3">
                Wir gewähren auf unsere handwerklichen Leistungen 2 Jahre Gewährleistung ab Abnahme. 
                Dies umfasst Material- und Verarbeitungsfehler.
              </p>
              <p className="text-gray-700">
                Ausgeschlossen von der Gewährleistung sind Schäden durch normale Abnutzung, 
                unsachgemäße Behandlung oder Einwirkungen Dritter.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-lg text-gray-900 mb-3">Haftung</h3>
              <p className="text-gray-700 mb-3">
                Wir haften für Schäden nur bei Vorsatz und grober Fahrlässigkeit. 
                Die Haftung für leichte Fahrlässigkeit ist auf die Verletzung wesentlicher 
                Vertragspflichten beschränkt.
              </p>
              <p className="text-gray-700">
                Wir sind vollversichert über unsere Betriebshaftpflichtversicherung.
              </p>
            </div>
          </div>
        </div>

        {/* Sonstiges */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8 border border-gray-100">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Schlussbestimmungen</h2>
          
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold text-lg text-gray-900 mb-3">Aufrechnung und Zurückbehaltung</h3>
              <p className="text-gray-700">
                Der Kunde kann nur mit unbestrittenen oder rechtskräftig festgestellten Forderungen aufrechnen. 
                Ein Zurückbehaltungsrecht kann nur wegen Gegenansprüchen aus dem gleichen Vertragsverhältnis geltend gemacht werden.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-lg text-gray-900 mb-3">Gerichtsstand und anzuwendendes Recht</h3>
              <p className="text-gray-700">
                Es gilt deutsches Recht unter Ausschluss des UN-Kaufrechts. 
                Gerichtsstand für alle Streitigkeiten ist unser Geschäftssitz in Aldingen.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-lg text-gray-900 mb-3">Salvatorische Klausel</h3>
              <p className="text-gray-700">
                Sollten einzelne Bestimmungen dieser AGB unwirksam sein oder werden, 
                berührt dies nicht die Wirksamkeit der übrigen Bestimmungen.
              </p>
            </div>
          </div>
        </div>

        {/* Kontakt */}
        <div className="bg-blue-50 rounded-xl p-8 border border-blue-100">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Kontakt bei Fragen</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Ansprechpartner:</h3>
              <ul className="text-gray-700 space-y-1">
                <li>Telefon: <a href="tel:+4915758183212" className="text-blue-600 hover:text-blue-700">+49 157 58183212</a></li>
                <li>WhatsApp: <a href="https://wa.me/4915758183212" className="text-blue-600 hover:text-blue-700" target="_blank" rel="noopener noreferrer">Direkt kontaktieren</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Geschäftszeiten:</h3>
              <p className="text-gray-700">Termine nach Vereinbarung</p>
            </div>
          </div>
        </div>

        {/* Stand */}
        <div className="text-center mt-12 pt-8 border-t border-gray-200">
          <p className="text-gray-500">
            Stand: {new Date().toLocaleDateString('de-DE', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </p>
        </div>
      </div>
    </div>
  );
};

export default AGB;